/** Emparelhar elementos quando o GlobalId muda entre revisões.
 *
 *  O problema. A comparação entre duas revisões casa elementos pelo
 *  GlobalId, que é o que a norma manda. Mas vários exportadores
 *  regeneram os GlobalId a cada exportação: o mesmo pilar, no mesmo
 *  sítio, com o mesmo nome, sai com um identificador novo. A
 *  comparação diz então que foi removido um pilar e acrescentado
 *  outro, e um modelo em que nada mudou aparece com centenas de
 *  alterações. O relatório fica inútil precisamente quando era mais
 *  preciso.
 *
 *  A ideia. Entre os que ficaram por casar de um lado e do outro,
 *  procura-se quem tem exactamente o mesmo conteúdo. Se de um lado há
 *  um só e do outro lado há um só com a mesma assinatura, são o mesmo
 *  elemento com identificador novo.
 *
 *  O que isto NÃO é. Não é adivinhação com margem: ou a assinatura é
 *  igual, ou não se empareja. Grupos com muitos candidatos iguais
 *  ficam por resolver e são reportados como ambíguos, em vez de se
 *  inventar um par. Um relatório que diz "não sei" é melhor do que um
 *  que diz mal.
 */

export interface ElementoPorCasar {
  globalId: string
  schemaType: string
  name: string | null
  objectType: string | null
  tag: string | null
  predefinedType: string | null
  container: string | null
  /** Centro da caixa envolvente, em milímetros, se for conhecido.
   *  Serve para desempatar candidatos iguais. */
  centro?: { x: number; y: number; z: number }
}

export type TipoDePar = 'renomeado' | 'renomeado-e-movido'

export interface ParPorConteudo {
  /** O que estava na revisão anterior. */
  antes: ElementoPorCasar
  /** O que está na revisão nova. */
  depois: ElementoPorCasar
  tipo: TipoDePar
  /** Distância entre os centros, em milímetros, quando se sabe. */
  deslocamentoMm?: number
}

export interface ResultadoEmparelhamento {
  pares: ParPorConteudo[]
  /** Assinaturas com candidatos a mais para se decidir com confiança. */
  ambiguos: {
    assinatura: string
    antes: ElementoPorCasar[]
    depois: ElementoPorCasar[]
  }[]
  /** Ficaram mesmo sem par: são adições e remoções verdadeiras. */
  semPar: { antes: ElementoPorCasar[]; depois: ElementoPorCasar[] }
}

/** Acima disto não se tenta emparelhar dentro de um grupo.
 *
 *  Com muitos candidatos idênticos, emparelhar por proximidade passa a
 *  ser adivinhação: mil parafusos iguais não têm maneira honesta de
 *  ser ligados um a um. */
export const MAXIMO_POR_GRUPO = 128

/** Acima desta distância não se aceita um par, mesmo com assinatura
 *  igual. Dez metros: um elemento que "andou" mais do que isto é mais
 *  provável ser outro elemento do que o mesmo deslocado. */
export const DESLOCAMENTO_MAXIMO_MM = 10_000

/** A assinatura de conteúdo de um elemento.
 *
 *  A geometria fica de fora de propósito. Um elemento que se moveu
 *  continua a ser o mesmo elemento, e se a posição entrasse na
 *  assinatura ele caía num grupo diferente do seu antecessor e nunca
 *  poderia ser emparelhado. A posição serve para desempatar dentro do
 *  grupo, não para o formar. */
export function assinatura(e: ElementoPorCasar): string {
  return [
    e.schemaType,
    e.name ?? '',
    e.objectType ?? '',
    e.tag ?? '',
    e.predefinedType ?? '',
    e.container ?? '',
  ].join('')
}

function distanciaMm(
  a: ElementoPorCasar,
  b: ElementoPorCasar,
): number | undefined {
  if (!a.centro || !b.centro) return undefined
  const dx = a.centro.x - b.centro.x
  const dy = a.centro.y - b.centro.y
  const dz = a.centro.z - b.centro.z
  return Math.sqrt(dx * dx + dy * dy + dz * dz)
}

function agrupar(
  elementos: readonly ElementoPorCasar[],
): Map<string, ElementoPorCasar[]> {
  const mapa = new Map<string, ElementoPorCasar[]>()
  for (const e of elementos) {
    const chave = assinatura(e)
    const lista = mapa.get(chave)
    if (lista) lista.push(e)
    else mapa.set(chave, [e])
  }
  return mapa
}

/** Empareja por vizinho mais próximo mútuo dentro de um grupo.
 *
 *  Mútuo e não simplesmente "mais próximo": se A escolhe B mas B
 *  escolhe C, não se aceita o par. É o que evita encadeamentos em que
 *  cada elemento rouba o vizinho do seguinte. */
function emparelharPorPosicao(
  antes: ElementoPorCasar[],
  depois: ElementoPorCasar[],
): { pares: [ElementoPorCasar, ElementoPorCasar, number][]; sobram: {
  antes: ElementoPorCasar[]
  depois: ElementoPorCasar[]
} } {
  const pares: [ElementoPorCasar, ElementoPorCasar, number][] = []
  const usadosAntes = new Set<string>()
  const usadosDepois = new Set<string>()

  const maisProximo = (
    e: ElementoPorCasar,
    candidatos: ElementoPorCasar[],
    usados: Set<string>,
  ): { alvo: ElementoPorCasar; d: number } | null => {
    let melhor: { alvo: ElementoPorCasar; d: number } | null = null
    for (const c of candidatos) {
      if (usados.has(c.globalId)) continue
      const d = distanciaMm(e, c)
      if (d === undefined || d > DESLOCAMENTO_MAXIMO_MM) continue
      if (!melhor || d < melhor.d) melhor = { alvo: c, d }
    }
    return melhor
  }

  for (const a of antes) {
    if (usadosAntes.has(a.globalId)) continue
    const escolhaDeA = maisProximo(a, depois, usadosDepois)
    if (!escolhaDeA) continue
    const escolhaDeB = maisProximo(escolhaDeA.alvo, antes, usadosAntes)
    if (!escolhaDeB || escolhaDeB.alvo.globalId !== a.globalId) continue
    usadosAntes.add(a.globalId)
    usadosDepois.add(escolhaDeA.alvo.globalId)
    pares.push([a, escolhaDeA.alvo, escolhaDeA.d])
  }

  return {
    pares,
    sobram: {
      antes: antes.filter((e) => !usadosAntes.has(e.globalId)),
      depois: depois.filter((e) => !usadosDepois.has(e.globalId)),
    },
  }
}

/**
 * Empareja, por conteúdo, o que a comparação por GlobalId deixou por
 * casar.
 *
 * `removidos` é o que existia antes e desapareceu; `acrescentados` é o
 * que aparece agora. Um par entre os dois quer dizer que o elemento
 * não desapareceu nem nasceu: mudou de identificador.
 */
export function emparelharPorConteudo(
  removidos: readonly ElementoPorCasar[],
  acrescentados: readonly ElementoPorCasar[],
  maximoPorGrupo = MAXIMO_POR_GRUPO,
): ResultadoEmparelhamento {
  const gruposAntes = agrupar(removidos)
  const gruposDepois = agrupar(acrescentados)

  const pares: ParPorConteudo[] = []
  const ambiguos: ResultadoEmparelhamento['ambiguos'] = []
  const sobramAntes: ElementoPorCasar[] = []
  const sobramDepois: ElementoPorCasar[] = []

  for (const [chave, antes] of gruposAntes) {
    const depois = gruposDepois.get(chave)
    if (!depois || depois.length === 0) {
      sobramAntes.push(...antes)
      continue
    }
    gruposDepois.delete(chave)

    // O caso simples e o mais comum: um de cada lado, sem dúvida
    // possível.
    if (antes.length === 1 && depois.length === 1) {
      const d = distanciaMm(antes[0], depois[0])
      pares.push({
        antes: antes[0],
        depois: depois[0],
        tipo: d !== undefined && d > 1 ? 'renomeado-e-movido' : 'renomeado',
        deslocamentoMm: d,
      })
      continue
    }

    if (antes.length > maximoPorGrupo || depois.length > maximoPorGrupo) {
      ambiguos.push({ assinatura: chave, antes, depois })
      continue
    }

    const { pares: porPosicao, sobram } = emparelharPorPosicao(antes, depois)
    for (const [a, b, d] of porPosicao) {
      pares.push({
        antes: a,
        depois: b,
        tipo: d > 1 ? 'renomeado-e-movido' : 'renomeado',
        deslocamentoMm: d,
      })
    }
    if (sobram.antes.length > 0 || sobram.depois.length > 0) {
      // Sem posições, ou com candidatos longe demais, não se inventa.
      ambiguos.push({
        assinatura: chave,
        antes: sobram.antes,
        depois: sobram.depois,
      })
    }
  }

  for (const depois of gruposDepois.values()) {
    sobramDepois.push(...depois)
  }

  return {
    pares,
    ambiguos,
    semPar: { antes: sobramAntes, depois: sobramDepois },
  }
}

/** Uma frase que resume o emparelhamento, para o painel. */
export function resumoEmparelhamento(r: ResultadoEmparelhamento): string {
  if (r.pares.length === 0 && r.ambiguos.length === 0) {
    return 'Nenhum elemento mudou de GlobalId entre as revisões.'
  }
  const partes: string[] = []
  if (r.pares.length > 0) {
    const movidos = r.pares.filter((p) => p.tipo === 'renomeado-e-movido').length
    const sitio =
      movidos === 0
        ? ''
        : `, ${movidos} ${movidos === 1 ? 'também mudou' : 'também mudaram'} de sítio`
    partes.push(
      `${r.pares.length} ${r.pares.length === 1 ? 'elemento manteve-se' : 'elementos mantiveram-se'} ` +
        `com GlobalId novo${sitio}`,
    )
  }
  if (r.ambiguos.length > 0) {
    const total = r.ambiguos.reduce((s, g) => s + g.antes.length, 0)
    partes.push(
      `${total} ficaram por decidir: há candidatos iguais a mais para emparelhar com confiança`,
    )
  }
  return `${partes.join('. ')}.`
}
